import { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import nlDict from "@/lib/i18n/nl";
import enDict from "@/lib/i18n/en";
import arDict from "@/lib/i18n/ar";

const STORAGE_KEY = "jobgrid_lang";
// UI languages the app fully supports. Additional codes fall back to `en`
// via the missing-key path in `t()`.
const UI_LANGS = ["nl", "en", "ar"];
const RTL_LANGS = new Set(["ar"]);

// Language dictionaries are kept in per-language modules under lib/i18n/
// so this file stays focused on the React provider + lookup logic. Adding
// a language = ship a new dict file, add it to UI_LANGS above and register
// it in this dict object; the fallback path in `t()` handles any missing
// keys by dropping back to English.
const dict = { nl: nlDict, en: enDict, ar: arDict };


const I18nContext = createContext(null);

function readStoredLang() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (UI_LANGS.includes(v)) return v;
  } catch {
    /* ignore */
  }
  return "nl";
}

export function I18nProvider({ children }) {
  const { user, setUser } = useAuth();
  const [lang, setLangState] = useState(readStoredLang);
  const lastServerSync = useRef(null);

  // Sync lang from authed user when login finishes / user refreshed
  useEffect(() => {
    if (user?.language && user.language !== lang && UI_LANGS.includes(user.language)) {
      setLangState(user.language);
      try { localStorage.setItem(STORAGE_KEY, user.language); } catch { /* ignore */ }
      lastServerSync.current = user.language;
    }
  }, [user?.language, lang]);

  // Keep <html lang> AND <html dir> in sync — dir=rtl kicks in for Arabic
  // so Tailwind's logical-inset/margin utilities behave naturally.
  useEffect(() => {
    try {
      document.documentElement.lang = lang;
      document.documentElement.dir = RTL_LANGS.has(lang) ? "rtl" : "ltr";
    } catch { /* ignore */ }
  }, [lang]);

  const setLang = useCallback(
    async (newLang) => {
      if (!UI_LANGS.includes(newLang)) return;
      setLangState(newLang);
      try { localStorage.setItem(STORAGE_KEY, newLang); } catch { /* ignore */ }

      // Persist server-side when authenticated
      if (user && newLang !== lastServerSync.current) {
        lastServerSync.current = newLang;
        try {
          const { data } = await api.patch("/auth/preferences", { language: newLang });
          if (setUser) setUser(data);
        } catch {
          // non-fatal — local change still applied
        }
      }
    },
    [user, setUser]
  );

  const t = useCallback(
    (key, params) => {
      const table = dict[lang] || dict.nl;
      let s = table[key];
      if (s === undefined) s = (dict.en[key] !== undefined ? dict.en[key] : key);
      if (params) {
        Object.keys(params).forEach((k) => {
          s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(params[k]));
        });
      }
      return s;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be inside I18nProvider");
  return ctx;
}
